import { useState } from "react";
import axios from "axios";
import { Phone, Mail, Clock, CheckCircle2, AlertTriangle } from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", website: "" });
  const [state, setState] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    setErrorMsg("");
    try {
      await axios.post(`${API}/contact`, form);
      setState("success");
    } catch (err) {
      setState("error");
      setErrorMsg(
        err.response?.status === 429
          ? "Too many attempts – please wait a while and try again, or call us."
          : "Something went wrong sending your enquiry. Please try again, or call us on 1800 957 474."
      );
    }
  };

  const inputCls =
    "w-full min-h-[48px] rounded border border-ink/15 bg-white px-4 py-3 text-base text-ink placeholder:text-steel/60 focus:outline-none focus:ring-2 focus:ring-road focus:border-road";

  return (
    <>
      <Seo
        title="Contact us | Reliance Motor Claims Recovery"
        description="Call 1800 957 474 or email claims@reliancemotorclaims.com.au. Open Mon–Fri 8:30am–5:30pm, Sat 9am–1pm. Or send us an enquiry online."
        path="/contact"
      />

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <p className="font-saira font-semibold text-xs sm:text-sm tracking-widest uppercase text-steel border-l-4 border-road pl-3">
            Contact
          </p>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-ink max-w-3xl">
            Talk to our claims team
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-steel max-w-2xl">
            Questions about an accident or an existing claim? Call us, email us, or send the
            form below and we'll be in touch.
          </p>
        </div>
      </section>

      <section className="bg-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Details */}
            <div className="space-y-4">
              <a
                href="tel:1800957474"
                className="flex items-center gap-4 bg-white rounded-lg p-5 sm:p-6 border border-transparent hover:border-road/40 transition-colors"
                data-testid="contact-phone-card"
              >
                <span className="inline-flex items-center justify-center size-12 bg-mist rounded-lg shrink-0">
                  <Phone className="size-6 text-ink" aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-saira font-semibold text-ink">Phone</span>
                  <span className="block text-steel">1800 957 474</span>
                </span>
              </a>
              <a
                href="mailto:claims@reliancemotorclaims.com.au"
                className="flex items-center gap-4 bg-white rounded-lg p-5 sm:p-6 border border-transparent hover:border-road/40 transition-colors"
                data-testid="contact-email-card"
              >
                <span className="inline-flex items-center justify-center size-12 bg-mist rounded-lg shrink-0">
                  <Mail className="size-6 text-ink" aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="block font-saira font-semibold text-ink">Email</span>
                  <span className="block text-steel break-all">claims@reliancemotorclaims.com.au</span>
                </span>
              </a>
              <div className="flex items-center gap-4 bg-white rounded-lg p-5 sm:p-6" data-testid="contact-hours-card">
                <span className="inline-flex items-center justify-center size-12 bg-mist rounded-lg shrink-0">
                  <Clock className="size-6 text-ink" aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-saira font-semibold text-ink">Opening hours</span>
                  <span className="block text-steel">Mon–Fri 8:30am–5:30pm</span>
                  <span className="block text-steel">Sat 9am–1pm</span>
                </span>
              </div>
            </div>

            {/* Form */}
            <div className="bg-white rounded-2xl p-6 sm:p-10 border-l-8 border-road">
              {state === "success" ? (
                <div className="py-8" data-testid="contact-success-message">
                  <CheckCircle2 className="size-12 text-ink" aria-hidden="true" />
                  <h2 className="mt-4 font-saira font-semibold text-2xl text-ink">
                    Thanks – we've received your enquiry.
                  </h2>
                  <p className="mt-3 text-steel text-base leading-relaxed">
                    Our claims team will be in touch shortly. If it's urgent, call us on{" "}
                    <a href="tel:1800957474" className="font-semibold text-ink underline decoration-road decoration-2 underline-offset-4">
                      1800 957 474
                    </a>
                    .
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} noValidate={false}>
                  <h2 className="font-saira font-semibold text-2xl text-ink">Send an enquiry</h2>

                  {/* Honeypot – invisible to humans */}
                  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
                    <label>
                      Website
                      <input
                        type="text"
                        name="website"
                        value={form.website}
                        onChange={update}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </label>
                  </div>

                  <div className="mt-6 space-y-5">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-semibold text-ink mb-1.5">
                        Name
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        autoComplete="name"
                        value={form.name}
                        onChange={update}
                        className={inputCls}
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-sm font-semibold text-ink mb-1.5">
                        Phone
                      </label>
                      <input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        autoComplete="tel"
                        value={form.phone}
                        onChange={update}
                        className={inputCls}
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-semibold text-ink mb-1.5">
                        Email
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={update}
                        className={inputCls}
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-semibold text-ink mb-1.5">
                        Message
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={5}
                        value={form.message}
                        onChange={update}
                        className={`${inputCls} min-h-[120px] resize-y`}
                        data-testid="contact-message-input"
                      />
                    </div>

                    {state === "error" && (
                      <div
                        className="flex items-start gap-3 bg-[#FDF3E3] border-l-4 border-road rounded p-4"
                        role="alert"
                        data-testid="contact-error-message"
                      >
                        <AlertTriangle className="size-5 mt-0.5 shrink-0 text-ink" aria-hidden="true" />
                        <p className="text-sm text-ink">{errorMsg}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={state === "sending"}
                      className="w-full inline-flex items-center justify-center min-h-[52px] px-8 bg-ink text-white font-saira font-bold text-base rounded hover:bg-black transition-colors disabled:opacity-60"
                      data-testid="contact-submit-button"
                    >
                      {state === "sending" ? "Sending…" : "Send enquiry"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
